/**
 * Geometry for the simulator's histogram of sample means with the normal curve the
 * central limit theorem predicts laid over it. Pure functions returning pixel positions
 * and an SVG path, so the drawing is deterministic and testable without a DOM.
 */

import {
  DENSITY_FRAME,
  type Frame,
  niceTicks,
  type Tick,
} from "@/lib/tool-kit/stats-distributions/chart";

export interface HistogramBar {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface HistogramGeometry {
  frame: Frame;
  bars: HistogramBar[];
  /** The expected counts under the normal curve, as an SVG path. */
  curve: string;
  baseline: number;
  ticks: Tick[];
  /** The vertical position of the largest value of the y scale, in counts. */
  ceiling: number;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The expected count in a bin of width w at x under N(mean, sd) for `total` values:
 * total × w × density. Used for the overlay curve and to size the y axis.
 */
export function expectedCount(
  x: number,
  mean: number,
  sd: number,
  total: number,
  binWidth: number
): number {
  const z = (x - mean) / sd;
  return (
    (total * binWidth * Math.exp(-0.5 * z * z)) / (sd * Math.sqrt(2 * Math.PI))
  );
}

/**
 * Bars for `counts` over [lo, hi] and the normal overlay.
 *
 * `scaleTotal` is how many values the full run will hold, `peakCount` the tallest bar of
 * the full run and `shownTotal` how many values are on screen now: the y axis is sized
 * for the full run, so during an animation the bars and the curve grow together instead
 * of the picture rescaling at every frame.
 */
export function histogramGeometry(
  counts: readonly number[],
  lo: number,
  hi: number,
  curve: { mean: number; sd: number },
  totals: { scaleTotal: number; shownTotal: number; peakCount: number },
  frame: Frame = DENSITY_FRAME
): HistogramGeometry {
  const bins = counts.length;
  const plotWidth = frame.width - frame.left - frame.right;
  const plotHeight = frame.height - frame.top - frame.bottom;
  const baseline = frame.height - frame.bottom;
  const binWidth = (hi - lo) / bins;
  const px = (x: number) => frame.left + ((x - lo) / (hi - lo)) * plotWidth;

  const fullPeak = expectedCount(
    curve.mean,
    curve.mean,
    curve.sd,
    totals.scaleTotal,
    binWidth
  );
  const ceiling = Math.max(fullPeak, totals.peakCount, 1, ...counts) * 1.08;
  const py = (count: number) => baseline - (count / ceiling) * plotHeight;

  const slot = plotWidth / bins;
  const bars = counts.map((count, index) => {
    const height = (count / ceiling) * plotHeight;
    return {
      index,
      x: round(frame.left + index * slot + slot * 0.04),
      y: round(baseline - height),
      width: round(slot * 0.92),
      height: round(height),
    };
  });

  const steps = 160;
  const points: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const x = lo + ((hi - lo) * i) / steps;
    const y = expectedCount(
      x,
      curve.mean,
      curve.sd,
      totals.shownTotal,
      binWidth
    );
    points.push(`${i === 0 ? "M" : "L"}${round(px(x))},${round(py(y))}`);
  }

  const ticks = niceTicks(lo, hi, 7).map((value) => ({
    position: round(px(value)),
    value,
  }));
  return { frame, bars, curve: points.join(""), baseline, ticks, ceiling };
}
